import { useState, useEffect } from "react";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAdminAuth } from "../services/admin/AdminAuthServices";
import { useNotification } from "../context/NotificationContext";


export default function AdminLogin() {
  const { showNotification } = useNotification();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const nextParam = queryParams.get("next") || null;
  const isExpired = queryParams.get("expired") === "true";

  const { adminLogin, loading, error, clearError } = useAdminAuth();

  useEffect(() => {
    if (isExpired) {
      showNotification({
        type: "warning",
        title: "Session Expired",
        message: "Your session has expired. Please log in again to continue."
      });
      // Clean up URL
      const newParams = new URLSearchParams(location.search);
      newParams.delete("expired");
      const search = newParams.toString();
      navigate(`${location.pathname}${search ? `?${search}` : ""}`, { replace: true });
    }
  }, [isExpired, showNotification, navigate, location.pathname, location.search]);




  useEffect(() => {
    clearError();
  }, [clearError]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      alert("All fields are required.");
      return;
    }

    const response = await adminLogin(email, password, nextParam);

    if (response?.status === "success") {
      const redirect = response.navigate_to || nextParam || "/admin/dashboard";
      navigate(redirect);
    }
  };


  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:from-[#0A0A0A] dark:via-[#111111] dark:to-[#1A1A1A] flex items-center justify-center p-4">
      {/* Background patterns - subtle brown accent */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#8B5E3C]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#8B5E3C]/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="w-full max-w-md relative">
        {/* Logo and Brand */}
        <div className="text-center mb-10 animate-fade-in">

          <div className="w-20 h-20 bg-white border border-[#E5E7EB] rounded-2xl flex items-center justify-center shadow-sm mx-auto mb-6 transform hover:scale-105 transition-transform duration-300 overflow-hidden">
            <img src="/static/images/temb_music_logo_transparent.png" alt="Temidayo Balogun" className="w-full h-full object-contain p-2" loading="eager" />
          </div>
          <h1 className="text-3xl font-normal text-[#111111] dark:text-white mb-2" style={{ fontFamily: "Playfair Display, Georgia, serif" }}>
            Admin Login
          </h1>
          <p className="text-black dark:text-[#A0A0A0] font-inter uppercase tracking-[2px] text-xs font-semibold">
            Temidayo Balogun
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-[#F0F0F0] dark:border-[#2A2A2A] p-8 md:p-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl p-4 animate-fade-in">
                <p className="text-red-600 dark:text-red-400 text-sm font-inter">
                  {error}
                </p>
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#111111] dark:text-white font-inter">
                Email Address
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-black dark:text-[#A0A0A0]"
                  size={16}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-black/60 dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-1 focus:ring-[#8B5E3C] focus:border-[#8B5E3C] transition-all duration-200"
                  placeholder="admin@temidayobalogun.com"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#111111] dark:text-white font-inter">
                Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-black dark:text-[#A0A0A0]"
                  size={16}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-4 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-black/60 dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-1 focus:ring-[#8B5E3C] focus:border-[#8B5E3C] transition-all duration-200"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-black hover:text-[#8B5E3C] dark:text-[#A0A0A0] transition-colors duration-200"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-[#E5E7EB] dark:border-[#2A2A2A] text-[#8B5E3C] focus:ring-[#8B5E3C] cursor-pointer"
                />
                <span className="text-xs text-black dark:text-[#A0A0A0] font-inter group-hover:text-[#111111] transition-colors">
                  Remember me
                </span>
              </label>
              <Link
                to="/admin/forgot-password"
                className="text-xs text-[#8B5E3C] hover:text-[#111111] dark:hover:text-[#A78BFA] transition-colors font-inter font-bold uppercase tracking-wider"
              >
                Forgot?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-6 bg-[#111111] hover:bg-[#8B5E3C] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 font-inter uppercase tracking-[2px] text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-black dark:text-[#666666] mt-10 font-inter uppercase tracking-widest">
          &copy; {new Date().getFullYear()} TEMB Music. All rights reserved.
        </p>
      </div>

      <style jsx global>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
    </div>
  );
}
